import { useEffect, useMemo, useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { X, UserPlus, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

/**
 * v84.50 — Armada Slot Stack (dumb component, no fetch)
 * Parent (CalendarBoard) fetch SEMUA assignments untuk day dalam 1 call,
 * lalu passthrough `assignment` object ke sini. Ini mencegah N+1 problem
 * (sebelumnya tiap armada fetch sendiri → N call).
 *
 * Props:
 *  - date         : "YYYY-MM-DD"
 *  - armadaId     : string (technician.id)
 *  - personnel    : Personnel[] (parent-cached)
 *  - breakLeaves  : BreakLeave[] hari itu (utk visualisasi "cuti" pd nama)
 *  - assignment   : {id, personnel_ids:[]} | undefined — dari parent map
 *  - reloadKey    : number (parent bump utk trigger reload)
 *  - onChanged    : callback setelah update sukses (parent akan re-fetch)
 */
export function ArmadaSlotStack({ date, armadaId, personnel = [], breakLeaves = [], assignment, reloadKey = 0, onChanged }) {
  const [slots, setSlots] = useState([null, null, null]);  // 3 rows of personnelId | null
  const [busy, setBusy] = useState(false);
  const [openIdx, setOpenIdx] = useState(null);

  const personnelMap = useMemo(() => {
    const m = {};
    for (const p of personnel) m[p.id] = p;
    return m;
  }, [personnel]);
  const breakIdSet = useMemo(() => new Set(breakLeaves.map(b => b.personnel_id)), [breakLeaves]);

  // v84.50 — Sync dari assignment prop (dari parent). Tidak fetch lagi.
  useEffect(() => {
    const ids = assignment?.personnel_ids || [];
    setSlots([ids[0] || null, ids[1] || null, ids[2] || null]);
  }, [assignment, reloadKey]);

  async function commit(newSlots) {
    setBusy(true);
    try {
      const personnel_ids = newSlots.filter(Boolean);
      await api.upsertArmadaAssignment({ date, armada_id: armadaId, personnel_ids });
      setSlots(newSlots);
      onChanged?.();      // Parent akan reload semua assignments dgn 1 call
    } catch (e) {
      toast.error("Gagal simpan: " + (e?.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  }

  function pick(idx, pid) {
    // Prevent same personnel di 2 slot berbeda armada yg sama
    if (slots.includes(pid)) {
      toast.error("Personel sudah ada di slot lain");
      return;
    }
    const next = [...slots]; next[idx] = pid; setOpenIdx(null); commit(next);
  }

  function clear(idx) {
    const next = [...slots]; next[idx] = null; commit(next);
  }

  // Available options: aktif personnel yg belum ada di slot manapun
  const available = personnel.filter(p => p.active !== false && !slots.includes(p.id));

  return (
    <div className="mt-1.5 space-y-0.5" data-testid={`armada-slots-${armadaId}`}>
      {slots.map((pid, i) => {
        const p = pid ? personnelMap[pid] : null;
        const onBreak = pid && breakIdSet.has(pid);
        return (
          <div key={i} className="flex items-center gap-1 group" data-testid={`armada-slot-${armadaId}-${i}`}>
            {p ? (
              <div className={`flex items-center gap-1 flex-1 min-w-0 px-1.5 py-0.5 rounded-sm text-[10px] font-medium border group ${
                onBreak
                  ? "bg-red-600 border-red-700 text-white line-through"       // v84.51 solid merah + font putih
                  : "bg-emerald-600 border-emerald-700 text-white"            // v84.51 solid hijau + font putih
              }`}>
                <span className="truncate" title={p.name + (onBreak ? " (cuti/break hari ini)" : "")}>{p.name}</span>
                <span className="text-[8px] font-mono opacity-80 ml-auto">{p.commission_percentage}%</span>
                <button onClick={() => clear(i)} disabled={busy}
                  className="opacity-0 group-hover:opacity-100 hover:text-yellow-200 transition-opacity"
                  data-testid={`armada-slot-remove-${armadaId}-${i}`}
                  title="Hapus dari slot">
                  <X className="w-2.5 h-2.5" />
                </button>
              </div>
            ) : (
              <Popover open={openIdx === i} onOpenChange={(v)=>setOpenIdx(v?i:null)}>
                <PopoverTrigger asChild>
                  <button
                    className="flex-1 flex items-center justify-center gap-1 px-1.5 py-0.5 rounded-sm text-[10px] text-zinc-500 border border-dashed border-border hover:border-blue-500/50 hover:text-blue-400 transition-colors"
                    data-testid={`armada-slot-add-${armadaId}-${i}`}
                    disabled={busy}>
                    {busy ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : <UserPlus className="w-2.5 h-2.5" />}
                    <span>Slot {i+1}</span>
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-64 p-0" align="start" data-testid={`armada-slot-popover-${armadaId}-${i}`}>
                  <div className="max-h-64 overflow-auto">
                    {available.length === 0 && (
                      <div className="p-3 text-[11px] text-muted-foreground">Tidak ada personel tersedia</div>
                    )}
                    {available.map(p => (
                      <button
                        key={p.id}
                        onClick={() => pick(i, p.id)}
                        className={`w-full text-left px-2.5 py-1.5 text-[12px] hover:bg-muted flex items-center justify-between gap-2 border-b border-border last:border-b-0 ${breakIdSet.has(p.id) ? "opacity-60" : ""}`}
                        data-testid={`armada-slot-pick-${armadaId}-${i}-${p.id}`}>
                        <span className="truncate">
                          {p.name}
                          {breakIdSet.has(p.id) && <span className="ml-1 text-[9px] text-red-400 font-mono">CUTI</span>}
                        </span>
                        <span className="text-[10px] font-mono text-muted-foreground flex-shrink-0">{p.commission_percentage}%</span>
                      </button>
                    ))}
                  </div>
                </PopoverContent>
              </Popover>
            )}
          </div>
        );
      })}
    </div>
  );
}
